import React from 'react';
import { Calendar, Star, CheckCircle, X, AlertCircle, MapPin, Clock, Users, ExternalLink, Edit } from 'lucide-react';
import { useEventImage } from '../hooks/useEventThumbnail';
import EventLocationViewer from './EventLocationViewer';



interface EventDetailModalProps {
  event: any;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (event: any) => void;
  canEdit?: boolean;
}

const EventDetailModal: React.FC<EventDetailModalProps> = ({ event, isOpen, onClose, onEdit, canEdit = false }) => {
  if (!isOpen || !event) return null;

  // Component for rendering event image with thumbnail API support
  const EventImage: React.FC = () => {
    const eventId = event.id || event.EVENT_ID;
    const originalEvent = event.originalEvent || event;
    const { thumbnailUrl, loading } = useEventImage(originalEvent, eventId);
    
    if (loading) {
      return (
        <div className="w-full h-64 bg-gray-200 animate-pulse flex items-center justify-center">
          <div className="text-gray-400 text-sm">Loading...</div>
        </div>
      );
    }
    
    return (
      <img
        src={thumbnailUrl || 'https://images.pexels.com/photos/6113659/pexels-photo-6113659.jpeg?auto=compress&cs=tinysrgb&w=600'}
        alt={event.title}
        className="w-full h-64 object-cover"
        onError={(e) => {
          console.error('❌ Image failed to load for event:', event.title);
          e.currentTarget.src = 'https://images.pexels.com/photos/6113659/pexels-photo-6113659.jpeg?auto=compress&cs=tinysrgb&w=600';
        }}
      />
    );
  };

  // Open map in external services
  const openInGoogleMaps = () => {
    const lat = Number(event.originalEvent?.latitude);
    const lng = Number(event.originalEvent?.longitude);
    window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`, '_blank');
  };


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white z-10">
          <div className="flex items-start space-x-3">
            <Calendar className="h-6 w-6 text-dpn-pink mt-0.5" />
            <div>
              <h2 className="text-2xl font-runtime font-bold text-gray-900 mb-1 line-clamp-2">{event.title}</h2>
            </div>
          </div>
          {/* Header Actions */}
          <div className="flex items-center space-x-2">
            {canEdit && onEdit && (
              <button
                onClick={() => onEdit(event)}
                className="flex items-center space-x-2 px-3 py-2 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg transition-colors font-medium text-sm"
                title="Edit Event"
              >
                <Edit className="h-4 w-4" />
                <span>Edit Event</span>
              </button>
            )}
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Left Column: Event Image */}
            <div>
              <div className="relative rounded-lg overflow-hidden">
                <EventImage />
              </div>

              {/* Description */}
              <div className="mt-4">
                <h3 className="font-medium text-lg text-black mb-3">About this event</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700 text-sm">{event.description || event.originalEvent?.description || 'No description available'}</p>
                </div>
              </div>
            </div>

            {/* Right Column: Event Details */}
            <div>
              <h3 className="font-medium text-lg text-black mb-3">Event Details</h3>
              <div className="space-y-3">

                
                {/* Event Status */}
                <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1 text-sm">
                    <p className="font-medium text-gray-900">Status</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {(event.isVerified || event.originalEvent?.isVerified) ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="h-3.5 w-3.5 mr-1" /> Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <AlertCircle className="h-3.5 w-3.5 mr-1" /> Pending Verification
                        </span>
                      )}
                      
                      {(event.isActive !== false && event.originalEvent?.isActive !== false) ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Inactive
                        </span>
                      )}
                      
                      {(event.isFeatured || event.originalEvent?.isFeatured) && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <Star className="h-3.5 w-3.5 mr-1" /> Featured
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Event Date */}
                <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <Clock className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div className="flex-1 text-sm">
                    <p className="font-medium text-gray-900">Event Date</p>
                    <p className="text-gray-600">
                      {event.originalEvent?.startDate && event.originalEvent?.endDate ? (
                        <>
                          Start: {new Date(event.originalEvent.startDate).toLocaleString()}<br />
                          End: {new Date(event.originalEvent.endDate).toLocaleString()}
                        </>
                      ) : event.date || 'Date not set'}
                    </p>
                  </div>
                </div>
                
                {/* Expected Attendance */}
                <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <Users className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div className="flex-1 text-sm">
                    <p className="font-medium text-gray-900">Expected Attendance</p>
                    <p className="text-gray-600">
                      {event.originalEvent?.expectedAttendance || 
                       event.attendees || 
                       'Open to all'}
                    </p>
                  </div>
                </div>
                
                {/* Location */}
                <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div className="flex-1 text-sm">
                    <p className="font-medium text-gray-900">Location</p>
                    <p className="text-gray-600">{event.location || event.originalEvent?.location || 'Location not set'}</p>
                    {event.originalEvent?.latitude && event.originalEvent?.longitude && (
                      <p className="text-gray-500 text-xs mt-1">
                        Coordinates: {event.originalEvent.latitude}, {event.originalEvent.longitude}
                      </p>
                    )}
                  </div>
                </div>
                

                
                {/* Timestamps */}
                <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1 text-sm">
                    <p className="font-medium text-gray-900">Timestamps</p>
                    <div className="text-gray-500 text-xs space-y-1">
                      {event.originalEvent?.createdAt && (
                        <p>Created: {new Date(event.originalEvent.createdAt).toLocaleString()}</p>
                      )}
                      {event.originalEvent?.updatedAt && (
                        <p>Updated: {new Date(event.originalEvent.updatedAt).toLocaleString()}</p>
                      )}
                      {event.originalEvent?.featuredAt && (
                        <p>Featured: {new Date(event.originalEvent.featuredAt).toLocaleString()}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Map Section - Full Width */}
          {event.originalEvent?.latitude && event.originalEvent?.longitude && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 p-4 flex items-center justify-between">
                <div className="flex items-center">
                  <MapPin className="h-5 w-5 text-dpn-pink mr-2" />
                  <span className="font-medium">Location Map: {event.location || event.originalEvent?.location}</span>
                  <span className="ml-3 text-xs bg-green-100 text-green-700 px-2.5 py-1 rounded-full font-medium">
                    Exact location
                  </span>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={openInGoogleMaps}
                    className="text-xs flex items-center text-blue-600 hover:text-blue-800"
                  >
                    <ExternalLink className="h-3.5 w-3.5 mr-1" /> Google Maps
                  </button>
                  
                </div>
              </div>
              
              {/* Map view */}
              <EventLocationViewer
                latitude={Number(event.originalEvent.latitude)}
                longitude={Number(event.originalEvent.longitude)}
                title={event.title}
                address={event.location || event.originalEvent?.location}
                height="h-80"
                showFullAddress={false}
                className="border-none"
              />
              
              {/* Map footer */}
              <div className="p-3 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                <span className="text-xs text-gray-500">
                  Coordinates: {event.originalEvent.latitude}, {event.originalEvent.longitude}
                </span>
                <button
                  onClick={openInGoogleMaps}
                  className="px-3 py-1 bg-dpn-pink hover:bg-dpn-raspberry text-white text-xs rounded-md transition-colors duration-200 flex items-center"
                >
                  <ExternalLink className="h-3 w-3 mr-1" /> View on Google Maps
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventDetailModal;
